import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReviewSidebar from './reviewSidebar';
import AsyncTable from '../../components/table/async';
import { SortTypeEnum } from '../../types';
import { mapFilters, mapSort } from './utils';
import { useLocation, useNavigate } from 'react-router';
import {
    ReportedActionRequestType,
    ReportedDetailsRequestType,
    ReportReviewListType,
    ReportReviewRequestType,
} from '../../types/report';
import { reportService } from '../../lib/api/report';

const ReviewReport = () => {
    const [loading, setLoading] = useState(true);

    const [data, setData] = useState<ReportReviewListType[]>([]);
    const [start, setStart] = useState(1);
    const [limit, setLimit] = useState(10);
    const [filters, setFilters] = useState<{ [key: string]: string } | null>(null);
    const [sort, setSort] = useState<{ [key: string]: SortTypeEnum } | null>(null);
    const [clearServerSearch, setClearServerSearch] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [reportedReviewData, setReportedReviewData] = useState<ReportedDetailsRequestType | null>(null);
    const [actionData, setActionData] = useState<ReportedActionRequestType | null>(null);
    const [suspendReasonText, setSuspendReasonText] = useState('');
    const location = useLocation();
    const navigate = useNavigate();

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = (data: ReportedDetailsRequestType, actionDataType: ReportReviewListType) => {
        setShow(true);
        setReportedReviewData(data);
        if (actionDataType) {
            setActionData({
                offerId: actionDataType?.offerId,
                currentStatus: actionDataType?.currentStatus,
                replyId: actionDataType.reviewCommentId || '',
                reportReplyId: actionDataType.reportReplyId || '',
                reportReviewId: actionDataType.reportReviewId,
                reviewId: actionDataType.reviewId,
                reviewReplyText: actionDataType.replyText || actionDataType.reviewText || '',
                reviewType: actionDataType.reviewType,
                suspendStatus: actionDataType.suspendStatus || 0,
            });
        }
    };

    const setOfferTypeStatus = (offerType: number) => {
        switch (offerType) {
            case 0:
                return 'Offer';

            case 1:
                return 'COE';

            case 2:
                return 'Coe on Offer';

            case 3:
                return 'Offline';

            case 4:
                return 'External';

            case 5:
                return 'Data Punch';

            default:
                return '';
        }
    };

    const setReviewTypeStatus = (reviewType: number) => {
        switch (reviewType) {
            case 0:
                return 'Candidate Review';

            case 1:
                return 'Company Review';

            default:
                return '';
        }
    };

    const setStatusMessage = (currentStatus: number) => {
        switch (currentStatus) {
            case 0:
                return 'Not Flagged';

            case 1:
                return 'Pending';

            case 2:
                return 'Deleted';

            case 3:
                return 'Retained';

            default:
                return '';
        }
    };

    const columns = useMemo(
        () => [
            {
                dataIndex: 'reportedBy',
                title: 'Reported By',
                render: (item: any) => (
                    <>
                        <div className="text-crop">
                            <div className="big-text" title={item.reportedBy}>
                                {item.reportedBy}
                            </div>
                        </div>
                    </>
                ),
            },
            {
                dataIndex: 'candidateName',
                title: 'Candidate Name',
                render: (item: any) => (
                    <>
                        <div className="text-crop">
                            <div className="big-text" title={item.candidateName}>
                                {item.candidateName}
                            </div>
                        </div>
                    </>
                ),
            },
            {
                dataIndex: 'companyName',
                title: 'Company Name',
                render: (item: any) => (
                    <>
                        <div className="text-crop">
                            <div className="big-text" title={item.companyName}>
                                {item.companyName}
                            </div>
                        </div>
                    </>
                ),
            },
            {
                dataIndex: 'offerType',
                title: 'Offer Type',
                render: (item: any) => (
                    <>
                        <div className="">{setOfferTypeStatus(item.offerType)}</div>
                    </>
                ),
            },
            {
                dataIndex: 'reviewType',
                title: 'Review Type',
                render: (item: any) => (
                    <>
                        <div className="">{setReviewTypeStatus(item.reviewType)}</div>
                    </>
                ),
            },
            {
                dataIndex: 'reportedDateFilter',
                title: 'Reported Date',
                // width: '128px',
                inputFilter: (value: any) => {
                    return new Date(value).toLocaleString('en-in', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    });
                },
                render: (item: any) =>
                    new Date(item.reportedDateFilter).toLocaleString('en-in', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    }),
            },
            {
                dataIndex: 'currentStatus',
                title: 'Status',
                render: (item: any) => (
                    <>
                        <div className="">{setStatusMessage(item.currentStatus)}</div>
                    </>
                ),
            },
            {
                dataIndex: '',
                title: 'Actions',
                render: (item: any) => (
                    <div className="text-center">
                        <button
                            className=""
                            type="button"
                            onClick={() => {
                                handleShow({ reportReviewType: item.reviewType, reviewId: item.reviewId }, item);
                                setSuspendReasonText(item?.suspendedReasons);
                            }}
                        >
                            <i className="bi bi-eye"></i>
                        </button>
                    </div>
                ),
            },
        ],
        [],
    );

    const getList = useCallback(async () => {
        try {
            !loading && setLoading(true);
            const params: ReportReviewRequestType = {
                Start: start,
                Limit: limit,
                ...filters,
                ...sort,
            };

            const {
                resultObject: { items, count },
            } = await reportService.getReviewsList(params);

            setData(items);
            setTotalCount(count);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }, [start, limit, filters, sort]);

    useEffect(() => {
        (async function () {
            await getList();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, limit, filters, sort]);
    return (
        <>
            <button
                onClick={() => {
                    setFilters(mapFilters([]));
                    sessionStorage.clear();
                    setClearServerSearch(true);
                }}
                className="btn btn-outline-primary btn-sm me-2 sticky-left"
            >
                <i className="bi bi-funnel"></i> Clear All
            </button>
            <div className="company-page-contener employer-table">
                <AsyncTable
                    loading={loading}
                    columns={columns}
                    data={data}
                    start={start}
                    setStart={setStart}
                    pageSize={limit}
                    clearServerSearch={clearServerSearch}
                    setClearServerSearch={setClearServerSearch}
                    setPageSize={setLimit}
                    totalCount={totalCount}
                    makeSearchRequest={(filters) => setFilters(mapFilters(filters))}
                    makeSortRequest={(value) => setSort(mapSort(value))}
                />
            </div>
            <div className="review-sidebar">
                <ReviewSidebar
                    type="reviewReport"
                    handleClose={handleClose}
                    show={show}
                    reportedData={reportedReviewData}
                    actionData={actionData}
                    suspendReasonText={suspendReasonText}
                    getList={getList}
                />
            </div>
        </>
    );
};

export default ReviewReport;
